/* eslint-disable react/jsx-no-useless-fragment */
import { Carer, Home, ResidentItem } from '@lmc/ts-common'
import { Avatar, Chevron, Tag, theme } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { GeneralText } from '../styledComponents'
import { EditTooltip } from './EditTooltip'
import { Avatar as AntdAvatar } from 'antd'
import useFeatureFlag from 'hooks/useFeatureFlags'

type GeneralZoneSectionProps = {
  resident: ResidentItem
  hasPro?: boolean
  testId?: string
}

export const GeneralZoneSection = ({
  resident,
  hasPro,
  testId,
}: GeneralZoneSectionProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const router = useRouter()

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers,
  )

  const flags = useFeatureFlag({
    flagNames: ['keyworkers'],
  })?.data

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )

  const flagsKeyworkersEnabled = flags?.keyworkers?.enabled

  const keyworkersEnabled =
    flagsKeyworkersEnabled && home?.features?.goals?.enabled

  const hasKeyworkers = !!resident?.keyworkers?.length

  return (
    <CoreSectionWrapper
      data-testid={testId ? `${testId}-zone-section` : 'resident-zone-section'}
    >
      <CoreSection
        data-testid={testId ? `${testId}-zone-info` : 'resident-zone-info'}
      >
        <StyledCoreLabel
          level={2}
          data-testid={testId ? `${testId}-zone-label` : 'resident-zone-label'}
        >
          {t(supConstants.ZONE)}
        </StyledCoreLabel>
        {!hasPro && (
          <Button
            variant="text"
            onClick={() => router.navigate('/settings/zones')}
            data-testid={
              testId ? `${testId}-zone-pro-button` : 'resident-zone-pro-button'
            }
          >
            {t(supConstants.PRO_FEATURE)}
            <Chevron direction="right" color={theme.color.status.primary} />
          </Button>
        )}
        {hasPro && (
          <GeneralText
            weight="bold"
            data-testid={
              testId ? `${testId}-zone-value` : 'resident-zone-value'
            }
          >
            <EditTooltip title={t(supConstants.ZONE_TOOLTIP)}>
              <>{resident?.zoneTitle ? resident?.zoneTitle : 'Unassigned'}</>
            </EditTooltip>
          </GeneralText>
        )}
      </CoreSection>
      <CoreSectionRow
        data-testid={
          testId ? `${testId}-location-details` : 'resident-location-details'
        }
      >
        {resident?.location?.building ? (
          <CoreSectionInner
            data-testid={
              testId
                ? `${testId}-building-section`
                : 'resident-building-section'
            }
          >
            <StyledCoreLabel
              level={2}
              data-testid={
                testId ? `${testId}-building-label` : 'resident-building-label'
              }
            >
              {t(supConstants.BUILDING)}
            </StyledCoreLabel>
            <EditTooltip title={t(supConstants.BUILDING_TOOLTIP)}>
              <StyledTag
                data-testid={
                  testId
                    ? `${testId}-building-value`
                    : 'resident-building-value'
                }
              >
                {resident.location.building}
              </StyledTag>
            </EditTooltip>
          </CoreSectionInner>
        ) : null}
        {resident?.location?.floor ? (
          <CoreSectionInner
            data-testid={
              testId ? `${testId}-floor-section` : 'resident-floor-section'
            }
          >
            <StyledCoreLabel
              level={2}
              data-testid={
                testId ? `${testId}-floor-label` : 'resident-floor-label'
              }
            >
              {t(supConstants.FLOOR)}
            </StyledCoreLabel>
            <EditTooltip title={t(supConstants.FLOOR_TOOLTIP)}>
              <StyledTag
                data-testid={
                  testId ? `${testId}-floor-value` : 'resident-floor-value'
                }
              >
                {resident.location.floor}
              </StyledTag>
            </EditTooltip>
          </CoreSectionInner>
        ) : null}
        {resident?.location?.room ? (
          <CoreSectionInner
            data-testid={
              testId ? `${testId}-room-section` : 'resident-room-section'
            }
          >
            <StyledCoreLabel
              level={2}
              data-testid={
                testId ? `${testId}-room-label` : 'resident-room-label'
              }
            >
              {t(supConstants.ROOM)}
            </StyledCoreLabel>
            <EditTooltip title={t(supConstants.ROOM_TOOLTIP)}>
              <StyledTag
                data-testid={
                  testId ? `${testId}-room-value` : 'resident-room-value'
                }
              >
                {resident.location?.room}
              </StyledTag>
            </EditTooltip>
          </CoreSectionInner>
        ) : null}
        {resident?.location?.bed ? (
          <CoreSectionInner
            data-testid={
              testId ? `${testId}-bed-section` : 'resident-bed-section'
            }
          >
            <StyledCoreLabel
              level={2}
              data-testid={
                testId ? `${testId}-bed-label` : 'resident-bed-label'
              }
            >
              {t(supConstants.BED)}
            </StyledCoreLabel>
            <EditTooltip title={t(supConstants.BED_TOOLTIP)}>
              <StyledTag
                data-testid={
                  testId ? `${testId}-bed-value` : 'resident-bed-value'
                }
              >
                {resident.location.bed}
              </StyledTag>
            </EditTooltip>
          </CoreSectionInner>
        ) : null}
      </CoreSectionRow>
      <CoreSection
        data-testid={
          testId ? `${testId}-care-team-section` : 'resident-care-team-section'
        }
      >
        <StyledCoreLabel
          level={2}
          data-testid={
            testId ? `${testId}-care-team-label` : 'resident-care-team-label'
          }
        >
          {t(supConstants.CARE_TEAM)}
        </StyledCoreLabel>
        <GeneralText
          weight="bold"
          data-testid={
            testId ? `${testId}-care-team-value` : 'resident-care-team-value'
          }
        >
          {resident?.assignAll ? (
            <EditTooltip title={t(supConstants.CARE_TEAM_TOOLTIP)}>
              <>{t(supConstants.ALL_CARERS)}</>
            </EditTooltip>
          ) : (
            <EditTooltip title={t(supConstants.CARE_TEAM_TOOLTIP)}>
              <AvatarsWrapper
                data-testid={
                  testId
                    ? `${testId}-care-team-avatars`
                    : 'resident-care-team-avatars'
                }
              >
                <AntdAvatar.Group maxCount={5} maxStyle={maxStyle}>
                  {resident?.carers?.map((residentCarerId) => (
                    <Avatar
                      key={residentCarerId}
                      url={
                        carers?.find((carer) => carer?.id === residentCarerId)
                          ?.picture as unknown as string
                      }
                      data-testid={
                        testId
                          ? `${testId}-carer-avatar-${residentCarerId}`
                          : `resident-carer-avatar-${residentCarerId}`
                      }
                    />
                  ))}
                </AntdAvatar.Group>
              </AvatarsWrapper>
            </EditTooltip>
          )}
        </GeneralText>
      </CoreSection>
      {keyworkersEnabled && (
        <CoreSection
          data-testid={
            testId
              ? `${testId}-keyworkers-section`
              : 'resident-keyworkers-section'
          }
        >
          <StyledCoreLabel
            level={2}
            data-testid={
              testId
                ? `${testId}-keyworkers-label`
                : 'resident-keyworkers-label'
            }
          >
            {t(supConstants.KEYWORKERS)}
          </StyledCoreLabel>
          <GeneralText
            weight="bold"
            data-testid={
              testId
                ? `${testId}-keyworkers-value`
                : 'resident-keyworkers-value'
            }
          >
            {hasKeyworkers ? (
              <EditTooltip title={t(supConstants.KEYWORKERS_TOOLTIP)}>
                <AvatarsWrapper
                  data-testid={
                    testId
                      ? `${testId}-keyworkers-avatars`
                      : 'resident-keyworkers-avatars'
                  }
                >
                  <AntdAvatar.Group maxCount={5} maxStyle={maxStyle}>
                    {resident?.keyworkers?.map((keyworkerId) => (
                      <Avatar
                        key={keyworkerId}
                        url={
                          carers?.find((carer) => carer?.id === keyworkerId)
                            ?.picture as unknown as string
                        }
                        data-testid={
                          testId
                            ? `${testId}-keyworker-avatar-${keyworkerId}`
                            : `resident-keyworker-avatar-${keyworkerId}`
                        }
                      />
                    ))}
                  </AntdAvatar.Group>
                </AvatarsWrapper>
              </EditTooltip>
            ) : (
              <EditTooltip title={t(supConstants.KEYWORKERS_TOOLTIP)}>
                <>{t(supConstants.NOT_ASSIGNED)}</>
              </EditTooltip>
            )}
          </GeneralText>
        </CoreSection>
      )}
    </CoreSectionWrapper>
  )
}

const StyledCoreLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const StyledTag = styled(Tag)`
  width: fit-content;
`

const CoreSectionWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  margin-top: 16px;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
  }
`

const CoreSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 15px;
  }
`

const CoreSectionRow = styled.div`
  display: flex;
  width: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 15px;
  }
`

const CoreSectionInner = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 5px;
`

const AvatarsWrapper = styled.div`
  display: flex;
`

const maxStyle = {
  color: theme.color.neutral.color3,
  backgroundColor: theme.color.neutral.color9,
}
